import type { Side, TraxMove, TraxState, TraxTile } from './trax';
const WHITE: Record<TraxTile, number> = {WhiteNS:5,WhiteEW:10,WhiteNE:3,WhiteES:6,WhiteSW:12,WhiteWN:9};
const DX = [0,1,0,-1], DY = [-1,0,1,0];
export interface TraxRoute { cells: [number,number][]; ends: [number,number][] }
/** Follow the color's two edges, retaining native trace()'s first empty-cell endpoints. */
export function traxRoutes(state: TraxState, color: Side): TraxRoute[] {
  const visited=new Set<number>(),routes:TraxRoute[]=[];
  const edges=(tile:TraxTile)=>color==='First'?WHITE[tile]:WHITE[tile]^15;
  for(let index=0;index<state.cells.length;index++) {
    if(!state.cells[index]||visited.has(index))continue;
    const cells:[number,number][]=[],ends:[number,number][]=[],pending=[index];
    while(pending.length) {
      const i=pending.pop()!;if(visited.has(i))continue;visited.add(i);
      const tile=state.cells[i];if(!tile)continue;const x=i%state.width,y=Math.floor(i/state.width);cells.push([x,y]);
      for(let d=0;d<4;d++) {
        if(!(edges(tile)&(1<<d)))continue;const nx=x+DX[d],ny=y+DY[d];
        const next=nx<0||ny<0||nx>=state.width||ny>=state.height?null:state.cells[ny*state.width+nx];
        if(!next||!(edges(next)&(1<<((d+2)%4))))ends.push([nx,ny]);
        else if(!visited.has(ny*state.width+nx))pending.push(ny*state.width+nx);
      }
    }
    routes.push({cells,ends});
  }
  return routes;
}
/** Mirrors TraxBoard::is_winning_line; path tile count is never a win condition. */
export function traxLineAxes(state:TraxState,route:TraxRoute):('width'|'height')[] {
  if(route.ends.length!==2)return [];
  const [a,b]=route.ends,axes:('width'|'height')[]=[];
  if(state.width>=8&&Math.abs(a[0]-b[0])>state.width)axes.push('width');
  if(state.height>=8&&Math.abs(a[1]-b[1])>state.height)axes.push('height');
  return axes;
}
/** Paint only the real Line outcome's winning color and a path touched by last_drop. */
export function traxWinningLines(state:TraxState):{cells:Set<string>;axes:Set<'width'|'height'>} {
  const cells=new Set<string>(),axes=new Set<'width'|'height'>();
  if(state.outcome?.kind!=='Line'||state.outcome.winner==null)return {cells,axes};
  const touched=new Set(state.last_drop.map(([x,y])=>`${x}-${y}`));
  for(const route of traxRoutes(state,state.outcome.winner)) {
    const winning=traxLineAxes(state,route);
    if(!winning.length||!route.cells.some(([x,y])=>touched.has(`${x}-${y}`)))continue;
    for(const [x,y] of route.cells)cells.add(`${x}-${y}`);
    for(const axis of winning)axes.add(axis);
  }
  return {cells,axes};
}

/** The entry is an occupied neighbour's touching edge; initial play consistently enters from north in white. */
export function traxCandidateGeometry(state: TraxState, move: TraxMove, tile: TraxTile): { entry: number; exit: number; color: Side; turn: 'straight' | 'left' | 'right' } {
  let entry = 0, color: Side = 'First';
  if (state.width && state.height) {
    const x = move.col - 1, y = move.row - 1;
    for (let d = 0; d < 4; d++) {
      const nx = x + DX[d], ny = y + DY[d];
      const neighbour = nx >= 0 && ny >= 0 && nx < state.width && ny < state.height ? state.cells[ny * state.width + nx] : null;
      if (!neighbour) continue;
      entry = d; color = WHITE[neighbour] & (1 << ((d + 2) % 4)) ? 'First' : 'Second'; break;
    }
  }
  const edges = color === 'First' ? WHITE[tile] : WHITE[tile] ^ 15;
  const exit = [0, 1, 2, 3].find(d => d !== entry && (edges & (1 << d))) ?? (entry + 2) % 4;
  const forward = (entry + 2) % 4;
  return { entry, exit, color, turn: exit === forward ? 'straight' : exit === (forward + 3) % 4 ? 'left' : 'right' };
}
